import React, {useState, useEffect, useMemo} from 'react'
import Select, {MenuListProps, components, MultiValue, ActionMeta, StylesConfig} from 'react-select'
import {useDispatch, useSelector} from 'react-redux'
import {useGetAllTagsQuery} from '../../../../../../services/cellcast'
import {getalltagsdata} from '../../Tags/TagSlice/TagsSlices'
import TagPopupModal from '../../AllContactsList/Modal/TagPopupModal'
import {Tag} from '../../../../../../services/cellcast.v2.types'
import {useI18n} from '../../../../../Utils/_helpers/useI18n'
import {TagOption, TagSelectProps, ReduxState, TagState} from './TagSelect.types'
import {filterTags, mapTagToOption} from './TagSelect.utils'
import './TagSelect.scss'

export const TagSelect: React.FC<TagSelectProps> = ({value, onChange, className}) => {
  const {t} = useI18n()
  const dispatch = useDispatch()
  const [openTagPopup, setOpenTagPopup] = useState(false)

  // Use API to get tags list
  const {
    data: apiTagData,
    isLoading: isTagDataLoading,
    refetch: refetchTags,
  } = useGetAllTagsQuery({})

  // Get tag list from Redux store
  const reduxTagsList = useSelector((state: ReduxState) => state.tags?.tagsList?.data)

  const [availableTags, setAvailableTags] = useState<TagState[]>([])

  useEffect(() => {
    if (apiTagData?.data) {
      const filteredTags = filterTags(apiTagData.data)
      setAvailableTags(filteredTags)
    }
  }, [apiTagData])

  useEffect(() => {
    if (reduxTagsList) {
      const filteredTags = filterTags(reduxTagsList)
      setAvailableTags(filteredTags)
    }
  }, [reduxTagsList])

  const tagOptions = useMemo(() => {
    return availableTags.map(mapTagToOption)
  }, [availableTags])

  const getBestTextColor = (bgColor: string): string => {
    const hex = bgColor.replace('#', '')
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance > 0.5 ? '#000000' : '#FFFFFF'
  }

  const customStyles: StylesConfig<TagOption, true> = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.data.color,
      color: getBestTextColor(state.data.color),
    }),
    multiValue: (provided, state) => ({
      ...provided,
      backgroundColor: state.data.color,
    }),
    multiValueLabel: (provided, state) => ({
      ...provided,
      color: getBestTextColor(state.data.color),
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      color: getBestTextColor(state.data.color),
    }),
  }

  const MenuList = (props: MenuListProps<TagOption, true>) => {
    return (
      <div className='tag-select'>
        <div className='tag-select-content'>{props.children}</div>
        <div
          className='tag-select-create-button d-flex align-items-center gap-2 justify-content-center cursor-pointer'
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setOpenTagPopup(true)
          }}
        >
          <i className='bi bi-plus-lg text-primary fw-bold'></i>
          <p className='text-primary fw-bold'>{t('RAW_CONTACT_COLLECTION.CREATE_NEW')}</p>
        </div>
      </div>
    )
  }

  const handleCloseTagPopup = () => {
    setOpenTagPopup(false)
    refetchTags()
    dispatch(getalltagsdata())
  }

  const handleChange = (newValue: MultiValue<TagOption>, actionMeta: ActionMeta<TagOption>) => {
    onChange(newValue as TagOption[])
  }

  return (
    <div className='tag-select p-0'>
      <TagPopupModal
        show={openTagPopup}
        handleClose={handleCloseTagPopup}
        selectedTag={value}
        setSelectedTag={onChange}
      />
      <Select<TagOption, true>
        value={value}
        components={{MenuList}}
        isMulti
        className={`select ${className}`}
        options={tagOptions}
        onChange={handleChange}
        isLoading={isTagDataLoading}
        styles={customStyles}
      />
    </div>
  )
}
